import { useEffect, useState } from 'react';
import { api, setCurrentScreen } from '../lib/api.js';
import { Chip } from '../components/ui.jsx';
import { SalesPoint } from '../spa/SalesPoint.jsx';
import { Transactions, Commission, CashUp, Reports } from '../spa/BackOffice.jsx';
import { Appointments } from '../spa/Appointments.jsx';
import { Drafts, Customers, Staff } from '../spa/Manage.jsx';
import { Catalogue, StaffRecords } from '../spa/Catalogue.jsx';
import { SendReport } from '../spa/SendReport.jsx';
import { ThisTill } from '../spa/ThisTill.jsx';
import { Users } from '../admin/Users.jsx';
import { Dashboard } from '../admin/Dashboard.jsx';
import { Inbox } from '../admin/Inbox.jsx';
import { AuditLog } from '../admin/AuditLog.jsx';
import { Tills } from '../admin/Tills.jsx';
import { onTill, watchConnection, syncNow } from '../lib/till.js';
import { ChangePassword } from './ChangePassword.jsx';

/**
 * The sidebar.
 *
 * Every item names the capability it needs. The server hands back which capabilities this
 * account has, and anything it does not have is not rendered at all: a menu item that
 * refuses you when clicked is worse than no menu item, because it invites the click.
 *
 * `null` means everybody who can sign in at all.
 */
const NAV = {
  services: [
    ['SELL', [['Sales point', 'sell'], ['Appointments', 'appointments'], ['Held drafts', 'drafts'],
              ['Transactions', 'sell'], ['Cash up', 'cashUp'], ['This machine', 'tillSetup']]],
    ['MANAGE', [['Customers', 'customers'], ['Price list', 'catalogue'], ['The team', 'staff'],
                ['Specialities', 'staff'], ['Commission', 'commission']]],
    ['REVIEW', [['Reports', 'reports'], ['Send a report up', 'sendReports'],
                ['Users and roles', 'onboard'], ['Audit log', 'auditLog'], ['Settings', 'businessSettings']]]
  ],
  retail: [
    ['SELL', [['Sales point', 'sell'], ['Transactions', 'sell'], ['Held drafts', 'drafts']]],
    ['STOCK', [['Products', 'catalogue'], ['Stock on hand', 'catalogue'], ['Purchases', 'catalogue']]],
    ['REVIEW', [['Reports', 'reports'], ['Send a report up', 'sendReports'],
                ['Users and roles', 'onboard'], ['Audit log', 'auditLog'], ['Settings', 'businessSettings']]]
  ],
  kitchen: [
    ['SELL', [['Sales point', 'sell'], ['Menu', 'catalogue'], ['Open orders', 'sell'], ['Transactions', 'sell']]],
    ['REVIEW', [['Reports', 'reports'], ['Send a report up', 'sendReports'],
                ['Users and roles', 'onboard'], ['Audit log', 'auditLog'], ['Settings', 'businessSettings']]]
  ],
  stay: [
    ['STAY', [['Availability', 'sell'], ['Reservations', 'sell'], ['Housekeeping', 'sell']]],
    ['REVIEW', [['Reports', 'reports'], ['Send a report up', 'sendReports'],
                ['Users and roles', 'onboard'], ['Audit log', 'auditLog'], ['Settings', 'businessSettings']]]
  ],
  jobs: [
    ['JOBS', [['Intake', 'sell'], ['Collection', 'sell']]],
    ['REVIEW', [['Reports', 'reports'], ['Send a report up', 'sendReports'], ['Settings', 'businessSettings']]]
  ],

  // The console. Not a seventh business: the thing that contains the other six.
  group: [
    ['THE WHOLE PLACE', [['Dashboard', 'dashboard'], ['Report inbox', 'inbox']]],
    ['PEOPLE', [['Users and roles', 'onboard']]],
    ['SYSTEM', [['Audit log', 'auditLog'], ['Businesses and tills', 'manageBusinesses'],
                ['Integrations', 'integrations']]]
  ]
};

const FIRST_SCREEN = { group: 'Dashboard', services: 'Sales point' };

/**
 * Screens that only mean something where a sale line is credited to a person. At the laundry
 * it is not: one cashier takes the bag, prices it and hands it back. A Commission screen with
 * nothing on it and a Specialities screen listing who can do what are worse than absent,
 * because they suggest a way of working the business does not have.
 *
 * Appointments is here for the same reason. It is a diary of who is booked with whom, which
 * is the spa's whole day and none of the laundry's: clothes are dropped off, not booked in
 * with a named person.
 */
const NEEDS_STAFF_CREDIT = new Set(['The team', 'Specialities', 'Commission', 'Appointments']);

/** Only means anything inside the till app, where there is a printer and a drawer wired on. */
const NEEDS_A_TILL = new Set(['This machine']);

/** Drop every item this account cannot reach, then drop any group left empty. */
function navFor(module, capabilities, { creditsStaff = true } = {}) {
  return (NAV[module] || NAV.services)
    .map(([label, items]) => [
      label,
      items
        .filter(([, cap]) => !cap || capabilities[cap])
        .filter(([name]) => creditsStaff || !NEEDS_STAFF_CREDIT.has(name))
        .filter(([name]) => onTill || !NEEDS_A_TILL.has(name))
        .map(([name]) => name)
    ])
    .filter(([, items]) => items.length);
}

export function Shell({ user, business, onLeave, onSignOut }) {
  const [caps, setCaps] = useState(null);
  // The console strip on the boarding screen can say which screen to land on, so clicking
  // "Users and roles" out there opens it in here rather than dropping you at the dashboard.
  const [active, setActive] = useState(business.openAt || null);
  const [pendingCart, setPendingCart] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [unread, setUnread] = useState(0);
  const openCartWith = cart => { setPendingCart(cart); setActive('Sales point'); };
  const accent = business.colour || 'var(--color-accent-default)';

  useEffect(() => {
    api.capabilities().then(d => setCaps(d.capabilities)).catch(() => setCaps({}));
    api.unreadCount().then(d => setUnread(d.unread)).catch(() => {});
  }, []);

  // Every screen somebody opens is recorded, and every call they make from it carries its
  // name, so an audit entry always says where in the app it came from.
  useEffect(() => {
    if (!active) return;
    setCurrentScreen(active);
    api.auditView(active, business.id);
  }, [active, business.id]);

  // SQLite has no booleans, so this arrives as 1 or 0. Absent means yes, which keeps every
  // business that predates the column behaving as it always did.
  const creditsStaff = business.credits_staff !== 0;
  const groups = caps ? navFor(business.module, caps, { creditsStaff }) : [];

  // Land on the first screen this account can actually reach, rather than on a fixed one
  // it may not have. Only runs once the capabilities are known.
  useEffect(() => {
    if (!groups.length) return;
    const reachable = groups.flatMap(([, items]) => items);
    if (active && reachable.includes(active)) return;
    const preferred = FIRST_SCREEN[business.module];
    setActive(reachable.includes(preferred) ? preferred : reachable[0]);
  }, [caps, business.module]);

  if (!caps) return <div style={{ padding: 40 }} className="t-body-m muted">Loading</div>;

  return (
    // app-shell so a phone can turn this row of columns into a stack. Untouched at 900 and
    // above, which is every size a counter is.
    <div className="app-shell" style={{ display: 'flex', height: '100%' }}>
      <aside className="app-sidebar" style={{
        flex: 'none', background: 'var(--color-bg-surface)',
        borderRight: '1px solid var(--color-border-default)', padding: '16px 12px', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px 14px' }}>
          <span style={{ width: 4, height: 24, borderRadius: 999, background: accent }} />
          <span className="t-heading-m" style={{ flex: 1 }}>{business.short_name}</span>
          <button className="btn btn-ghost btn-m" style={{ padding: '4px 8px' }} onClick={onLeave}>Switch</button>
        </div>

        {groups.map(([label, items]) => (
          <NavGroup key={label} label={label} items={items} active={active} onPick={setActive}
            badges={{ 'Report inbox': unread }} />
        ))}
      </aside>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{
          height: 'var(--size-topbar)', flex: 'none', display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 24px', background: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-default)'
        }}>
          <span className="t-heading-m">{active}</span>
          <span style={{ flex: 1 }} />
          <Connection />
          <button className="btn btn-ghost btn-m t-label-l secondary" onClick={() => setChangingPassword(true)}
            title="Change your password">{user.name}</button>
          <button className="btn btn-ghost btn-m" onClick={onSignOut}>Sign out</button>
        </header>

        {changingPassword && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(2,6,23,0.45)', overflowY: 'auto' }}>
            <ChangePassword user={user} onDone={() => setChangingPassword(false)}
              onCancel={() => setChangingPassword(false)} onSignOut={onSignOut} />
          </div>
        )}

        <div style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          <Screen screen={active} business={business} user={user} caps={caps}
            pendingCart={pendingCart} onConsumedCart={() => setPendingCart(null)} openCartWith={openCartWith} />
        </div>
      </main>
    </div>
  );
}

/**
 * A collapsible group. Open state is remembered per business, so the sidebar looks the way
 * you left it. The group holding the current screen is always forced open: collapsing the
 * thing you are looking at would be a strange way to lose your place.
 */
function NavGroup({ label, items, active, onPick, badges = {} }) {
  const key = 'epms.nav.' + label;
  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(key);
    return stored === null ? true : stored === '1';
  });
  const holdsActive = items.includes(active);
  const expanded = open || holdsActive;

  const toggle = () => {
    const next = !expanded;
    setOpen(next);
    localStorage.setItem(key, next ? '1' : '0');
  };

  return (
    <div>
      <button className="nav-group" onClick={toggle} aria-expanded={expanded}>
        <span>{label}</span>
        {!expanded && <span className="count">{items.length}</span>}
        <Chevron />
      </button>
      {expanded && (
        <div style={{ display: 'grid', gap: 2, paddingBottom: 2 }}>
          {items.map(item => (
            <button key={item} className="nav-item" aria-current={item === active}
              onClick={() => onPick(item)}>
              <span className="rail" />
              <span style={{ flex: 1 }}>{item}</span>
              {badges[item] > 0 && <Chip tone="danger">{badges[item]}</Chip>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Chevron = () => (
  <svg className="chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Connected, or not, and what is waiting.
 *
 * This used to be a green chip that said Online whatever was happening, which is worse than
 * nothing: it is the one thing on screen a cashier would check before deciding whether to
 * trust the till, and it was decoration. Now it is the truth, and when sales are queued it
 * says how many and offers to send them, because "will my sales be lost" is the question
 * somebody will actually be asking.
 *
 * In a browser there is no queue and no local store, so it stays as it was.
 */
function Connection() {
  const [state, setState] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => watchConnection(setState), []);

  if (!onTill || !state) return <Chip tone="success">Online</Chip>;

  const waiting = state.pending || 0;

  if (state.online === false) {
    return (
      <Chip tone="danger">
        No connection{waiting ? `, ${waiting} sale${waiting === 1 ? '' : 's'} waiting` : ''}
      </Chip>
    );
  }

  if (waiting) {
    return (
      <button className="btn btn-ghost btn-m" disabled={sending}
        title={`Oldest waiting is ${state.oldestPending}`}
        onClick={async () => { setSending(true); await syncNow(); setSending(false); }}>
        <Chip tone="warning">
          {sending ? 'Sending' : `${waiting} sale${waiting === 1 ? '' : 's'} to send`}
        </Chip>
      </button>
    );
  }

  return <Chip tone="success">Online</Chip>;
}

function Screen({ screen, business, user, caps, pendingCart, onConsumedCart, openCartWith }) {
  const slug = business.slug;
  const creditsStaff = business.credits_staff !== 0;

  // Above any one business. These are the same screen wherever you open them from.
  if (screen === 'Dashboard' && business.module === 'group') return <Dashboard />;
  if (screen === 'Report inbox') return <Inbox />;
  if (screen === 'Users and roles') return <Users user={user} />;
  if (screen === 'Audit log') return <AuditLog canRead={caps.auditLog} />;
  if (screen === 'Businesses and tills') return <Tills />;

  // Phase 1 built these for the services module, starting at Ezel Spa.
  if (business.module === 'services') {
    if (screen === 'Sales point') return (
      <SalesPoint slug={slug} user={user} creditsStaff={creditsStaff}
        initialCart={pendingCart} onConsumedCart={onConsumedCart} />
    );
    if (screen === 'Appointments') return <Appointments slug={slug} onCheckedIn={openCartWith} />;
    if (screen === 'Held drafts') return <Drafts slug={slug} onResume={openCartWith} />;
    if (screen === 'Transactions') return <Transactions slug={slug} canVoid={caps.voids} creditsStaff={creditsStaff} />;
    if (screen === 'Cash up') return <CashUp slug={slug} />;
    if (screen === 'This machine') return <ThisTill />;
    if (screen === 'Customers') return <Customers slug={slug} />;
    if (screen === 'Price list') return <Catalogue slug={slug} canEdit={caps.catalogue} />;
    if (screen === 'The team') return <StaffRecords slug={slug} canEdit={caps.staff} />;
    if (screen === 'Specialities') return <Staff slug={slug} canEdit={caps.staff} />;
    if (screen === 'Commission') return <Commission slug={slug} />;
    if (screen === 'Reports') return <Reports slug={slug} creditsStaff={creditsStaff} />;
    if (screen === 'Send a report up') return <SendReport slug={slug} />;
  }

  return <Placeholder screen={screen} business={business} />;
}

function Placeholder({ screen, business }) {
  return (
    <div className="card" style={{ padding: 40, display: 'grid', gap: 8, placeItems: 'center', textAlign: 'center' }}>
      <div className="t-heading-m">{screen}</div>
      <div className="t-body-m muted" style={{ maxWidth: 560 }}>
        Designed and not yet built. The services module is the one that is finished, at Ezel
        Spa. {business.short_name} follows once its own module is built.
      </div>
    </div>
  );
}

